// ABOUTME: Account management service for user data deletion (TD3-122)
// ABOUTME: Handles LGPD-compliant account deletion with 30-day recovery period

import pb from './pocketbaseClient';
import ErrorHandler from './errorHandler';
import { safeFilter } from '../utils/pbFilterEscape'; // TD3-191: SQL injection prevention

class AccountManager {
  /**
   * Verify user password before sensitive operations
   * @param {string} email - User email
   * @param {string} password - Password to verify
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async verifyPassword(email, password) {
    try {
      // Attempt authentication to verify password
      await pb.collection('OracleUsers').authWithPassword(email, password);
      return { success: true };
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.AUTH,
        `Password verification failed for account deletion: ${error.message}`,
        null,
        { email, errorStack: error.stack, operation: 'delete_account_verification' }
      );

      // Return Portuguese error message
      if (error.message.includes('Invalid') || error.message.includes('Failed to authenticate')) {
        return { success: false, error: 'Senha incorreta, tente novamente' };
      }

      return { success: false, error: 'Erro ao verificar senha. Tente novamente.' };
    }
  }

  /**
   * Mark user account for deletion (LGPD-compliant)
   * Sets 30-day recovery period before permanent deletion
   * @param {string} userId - User ID to delete
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async markAccountForDeletion(userId) {
    try {
      // Update user record to pending_deletion status
      await pb.collection('OracleUsers').update(userId, {
        accountStatus: 'pending_deletion',
        deletionRequested: true,
        deletionRequestedAt: new Date().toISOString(),
        isActive: false // Also disable account immediately
      });

      return { success: true };
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to mark account for deletion: ${error.message}`,
        userId,
        { errorStack: error.stack, operation: 'mark_account_deletion' }
      );

      return {
        success: false,
        error: 'Erro ao processar exclusão da conta. Tente novamente.'
      };
    }
  }

  /**
   * Complete account deletion process (LGPD-compliant simplified approach)
   * Marks account for deletion - hard delete happens after 30 days via backend job
   * @param {string} userId - User ID to delete
   * @param {string} email - User email
   * @param {string} password - User password for verification
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  static async deleteAccount(userId, email, password) {
    try {
      // Step 1: Verify password
      const verification = await this.verifyPassword(email, password);
      if (!verification.success) {
        return verification;
      }

      // Step 2: Mark account for deletion (30-day recovery window)
      const deletionResult = await this.markAccountForDeletion(userId);
      if (!deletionResult.success) {
        return deletionResult;
      }

      // Step 3: Account is now marked pending_deletion with isActive=false
      // Hard deletion of all data (user + sessions + logs) happens after 30 days
      // This is LGPD-compliant - no anonymization needed if deleting everything

      return {
        success: true,
        message: 'Sua conta foi marcada para exclusão. Você tem 30 dias para recuperá-la fazendo login novamente.'
      };
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.SYSTEM,
        `Account deletion failed: ${error.message}`,
        userId,
        { email, errorStack: error.stack, operation: 'mark_deletion' }
      );

      return {
        success: false,
        error: 'Erro ao processar exclusão da conta. Entre em contato com o suporte.'
      };
    }
  }

  /**
   * Recover account within 30-day window
   * @param {string} userId - User ID to recover
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async recoverAccount(userId) {
    try {
      await pb.collection('OracleUsers').update(userId, {
        accountStatus: 'active',
        deletionRequested: false,
        deletionRequestedAt: null,
        isActive: true // Re-enable account on recovery
      });

      return { success: true };
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to recover account: ${error.message}`,
        userId,
        { errorStack: error.stack, operation: 'recover_account' }
      );

      return {
        success: false,
        error: 'Erro ao recuperar conta. Entre em contato com o suporte.'
      };
    }
  }

  /**
   * Change user email address (LGPD Art. 18, III - Right to Correction)
   * @param {string} userId - User ID
   * @param {string} currentEmail - Current email (for verification)
   * @param {string} newEmail - New email address
   * @param {string} password - Password for security verification
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  static async changeEmail(userId, currentEmail, newEmail, password) {
    try {
      // 1. Verify password
      const verification = await this.verifyPassword(currentEmail, password);
      if (!verification.success) {
        return { success: false, error: 'Senha incorreta' };
      }

      // 2. Validate new email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        return { success: false, error: 'Formato de e-mail inválido' };
      }

      // 3. Check if new email is the same as current
      if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
        return { success: false, error: 'O novo e-mail não pode ser igual ao atual' };
      }

      // 4. Check if new email is already in use
      // TD3-191: Use safeFilter to prevent SQL injection
      try {
        const existingUser = await pb.collection('OracleUsers').getFirstListItem(
          safeFilter`email = "${newEmail}"`,
          { requestKey: null }
        );

        if (existingUser && existingUser.id !== userId) {
          return { success: false, error: 'Este e-mail já está em uso' };
        }
      } catch (error) {
        // No existing user found - this is good! 404 means email is available
        if (error.status !== 404) {
          throw error; // Re-throw if it's not a 404
        }
        // 404 is expected - email is not in use, continue with change
      }

      // 5. Use PocketBase's built-in email change method
      // This automatically handles email update + verification in one step
      await pb.collection('OracleUsers').requestEmailChange(newEmail);

      // 7. Send security notification to old email address (TD3-129 LGPD compliance)
      try {
        // Get user's nickname for personalized email
        const user = await pb.collection('OracleUsers').getOne(userId);
        const userName = user.nickname || '';

        const notificationResponse = await fetch('https://oraculo.buenaar.app/claude/api/send-email-change-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            oldEmail: currentEmail,
            userName: userName,
            newEmail: newEmail
          })
        });

        if (!notificationResponse.ok) {
          console.warn('Failed to send notification to old email - non-blocking error');
        } else {
          // TD3-191: Removed email from log to prevent PII exposure
          console.log(`✅ Security notification sent to old email`);
        }
      } catch (notificationError) {
        // Don't fail email change if notification fails - this is non-critical
        console.warn('Failed to send old email notification:', notificationError);
      }

      // TD3-191: Removed email addresses from log to prevent PII exposure
      console.log(`✅ Email changed successfully for user ${userId}`);

      return {
        success: true,
        message: 'E-mail alterado com sucesso! Verifique sua caixa de entrada para confirmar o novo endereço.'
      };
    } catch (error) {
      console.error('Email change error:', error);

      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Email change failed: ${error.message}`,
        userId,
        { currentEmail, newEmail, errorStack: error.stack }
      );

      return {
        success: false,
        error: 'Erro ao alterar e-mail. Tente novamente.'
      };
    }
  }

  /**
   * Check if account is pending deletion
   * @param {Object} user - User object from PocketBase
   * @returns {boolean}
   */
  static isAccountPendingDeletion(user) {
    return user?.accountStatus === 'pending_deletion' && user?.deletionRequested === true;
  }

  /**
   * Get days remaining until permanent deletion
   * @param {string} deletionRequestedAt - ISO timestamp of deletion request
   * @returns {number} Days remaining (0-30)
   */
  static getDaysRemainingForRecovery(deletionRequestedAt) {
    if (!deletionRequestedAt) return 0;

    const requestDate = new Date(deletionRequestedAt);
    const now = new Date();
    const daysPassed = Math.floor((now - requestDate) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, 30 - daysPassed);

    return daysRemaining;
  }

  /**
   * Hard delete expired accounts (TD3-122 - 30-day cleanup)
   * Permanently removes all user data after 30-day recovery period
   * @returns {Promise<{success: boolean, deletedCount: number, details: Array}>}
   */
  static async hardDeleteExpiredAccounts() {
    try {
      // Calculate 30 days ago
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Find accounts pending deletion for 30+ days
      const expiredAccounts = await pb.collection('OracleUsers').getFullList({
        filter: `accountStatus = "pending_deletion" && deletionRequestedAt <= "${thirtyDaysAgo.toISOString()}"`
      });

      if (expiredAccounts.length === 0) {
        return { success: true, deletedCount: 0, details: [] };
      }

      const deletionResults = [];

      // Hard delete each expired account
      for (const user of expiredAccounts) {
        try {
          // Delete all OracleSessions for this user
          // TD3-191: Use safeFilter to prevent SQL injection
          const sessions = await pb.collection('OracleSessions').getFullList({
            filter: safeFilter`userId = "${user.id}"`
          });

          for (const session of sessions) {
            await pb.collection('OracleSessions').delete(session.id);
          }

          // Delete all OracleUsageLog entries for this user
          // TD3-191: Use safeFilter to prevent SQL injection
          const logs = await pb.collection('OracleUsageLog').getFullList({
            filter: safeFilter`userId = "${user.id}"`
          });

          for (const log of logs) {
            await pb.collection('OracleUsageLog').delete(log.id);
          }

          // Finally, delete the user record
          await pb.collection('OracleUsers').delete(user.id);

          deletionResults.push({
            userId: user.id,
            email: user.email,
            deletedAt: new Date().toISOString(),
            sessionsDeleted: sessions.length,
            logsDeleted: logs.length,
            success: true
          });

        } catch (deleteError) {
          deletionResults.push({
            userId: user.id,
            email: user.email,
            success: false,
            error: deleteError.message
          });

          await ErrorHandler.captureError(
            ErrorHandler.ERROR_TYPES.DATABASE,
            `Failed to hard delete account ${user.id}: ${deleteError.message}`,
            user.id,
            { errorStack: deleteError.stack, operation: 'hard_delete_expired' }
          );
        }
      }

      return {
        success: true,
        deletedCount: deletionResults.filter(r => r.success).length,
        details: deletionResults
      };

    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.SYSTEM,
        `Hard delete expired accounts failed: ${error.message}`,
        null,
        { errorStack: error.stack, operation: 'hard_delete_batch' }
      );

      return {
        success: false,
        deletedCount: 0,
        details: [],
        error: error.message
      };
    }
  }

  /**
   * TEST HELPER: Simulate 30-day expiration by backdating deletionRequestedAt
   * WARNING: Only use for testing! This modifies deletion timestamps.
   * @param {string} userId - User ID to backdate
   * @returns {Promise<{success: boolean}>}
   */
  static async TEST_simulateExpiredDeletion(userId) {
    try {
      const thirtyOneDaysAgo = new Date();
      thirtyOneDaysAgo.setDate(thirtyOneDaysAgo.getDate() - 31);

      await pb.collection('OracleUsers').update(userId, {
        deletionRequestedAt: thirtyOneDaysAgo.toISOString()
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default AccountManager;
